import { FC, useEffect } from 'react';
import { FormControl, useToast } from '@chakra-ui/react';
import { useSplunkActivityLogger } from '@resolutionlife/ui';
import debounce from 'lodash.debounce';
import { head, isEmpty, isNil } from 'ramda';
import { createSearchParams, useNavigate } from 'react-router-dom';

import * as ToastConfigs from '../../configs/toast';
import { useGetSearchResultsLazyQuery } from '../../graphql/generated';
import { navigateToSearchPage } from '../../pages/search-results-page/search-results-page';
import {
  AutoComplete,
  AutoCompleteMenuItem,
} from '../autocomplete-input/autocomplete-input';
import {
  formatSearchResults,
  useGetCedantContextForSearch,
} from './file-search-autocomplete.utils';

const CHARACTER_LIMIT = 150;
const DELAY = 400;

export const FileSearchAutoComplete: FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const treatyContext = useGetCedantContextForSearch();
  const activityLogger = useSplunkActivityLogger();
  const [search, { error, loading, data }] = useGetSearchResultsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const debounceSearch = debounce((params) => search(params), DELAY);

  useEffect(() => {
    if (error) {
      toast(ToastConfigs.problemCompletingRequest);
    }
  }, [error, toast]);

  const handleSelectItem = (
    item: AutoCompleteMenuItem,
    inputValue?: string
  ) => {
    if (item.type === 'FILE') {
      navigate(item.value);
    }

    if (item.type === 'DIRECTORY') {
      navigate(item.value);
    }

    if (item.type === 'FULL_SEARCH_DEEPLINK') {
      handlePerformFullSearch(inputValue || '');
    }
  };

  const handlePerformFullSearch = (inputValue: string) => {
    const treaty = treatyContext;
    activityLogger.logFileSearch({ cedantId: treaty.join(','), query: inputValue });
    const searchParams = createSearchParams({ q: inputValue, treaty });
    navigate(navigateToSearchPage(searchParams), { replace: true });
  };

  const handleInputValueChange = (value?: string) => {
    if (isEmpty(value) || isNil(value)) return;

    debounceSearch({
      variables: {
        term: value,
        cedants: treatyContext,
        pagination: { offset: 0, limit: 5 },
      },
    });
  };

  const handleRefetch = (searchTerm: string) => {
    if (isEmpty(searchTerm) || isNil(searchTerm)) return;

    search({
      variables: {
        term: searchTerm,
        cedants: treatyContext,
        pagination: { offset: 0, limit: 5 },
      },
    });
  };

  const searchGlobally = treatyContext.length !== 1;
  const treatyPlaceholder = !searchGlobally && `Search ${head(treatyContext)}`;
  const items = formatSearchResults(data?.search.objects);

  return (
    <FormControl w="80">
      <AutoComplete
        items={items}
        isLoading={loading}
        onFocus={handleRefetch}
        onSelectItem={handleSelectItem}
        onSubmit={handlePerformFullSearch}
        onInputValueChange={handleInputValueChange}
        inputProps={{ maxLength: CHARACTER_LIMIT }}
        placeholder={treatyPlaceholder || 'Search all treaties'}
      />
    </FormControl>
  );
};
